import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiRequestError } from '../lib/api';
import { Callout, EmptyState, ErrorNotice, Spinner } from '../components/ui';

interface EmailTestResult {
  sent: boolean;
  to: string;
  from: string;
  replyTo: string | null;
}

interface FlaggedQuestion {
  id: string;
  examCode: string;
  domain: { code: string; name: string };
  questionType: string;
  difficulty: string;
  stem: string;
  options: string[] | null;
  correctAnswer: string;
  explanation: string;
  status: string;
  source: string;
  generatorModel: string | null;
  tags: string[];
  timesServed: number;
  timesCorrect: number;
  correctRatePct: number | null;
  flaggedAt: string | null;
  flagReason: string | null;
}

export function Admin() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailResult, setEmailResult] = useState<EmailTestResult | null>(null);
  const [emailNotice, setEmailNotice] = useState<string | null>(null);

  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => api.get<Record<string, unknown>>('/api/admin/stats'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['admin-flagged'],
    queryFn: () => api.get<{ questions: FlaggedQuestion[] }>('/api/admin/questions/flagged'),
  });

  const act = async (id: string, action: 'approve' | 'retire') => {
    setError(null);
    try {
      await api.post(`/api/admin/questions/${id}/${action}`);
      await qc.invalidateQueries({ queryKey: ['admin-flagged'] });
      await qc.invalidateQueries({ queryKey: ['admin-stats'] });
    } catch {
      setError(`Could not ${action} that question.`);
    }
  };

  const refill = async () => {
    setError(null);
    try {
      await api.post('/api/admin/pool/refill');
    } catch {
      setError('Could not queue a refill job.');
    }
  };

  const sendTestEmail = async () => {
    setEmailBusy(true);
    setEmailResult(null);
    setEmailNotice(null);
    try {
      const result = await api.post<EmailTestResult>('/api/admin/email/test');
      setEmailResult(result);
    } catch (err) {
      // A 503 here means RESEND_API_KEY isn't set — an expected configuration
      // state, not a failure, so it gets a neutral notice rather than the red
      // error banner used for the flagged-question actions above.
      setEmailNotice(err instanceof ApiRequestError ? err.message : 'Could not send the test email.');
    } finally {
      setEmailBusy(false);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">Question review queue</h1>
          <p className="mt-2 max-w-2xl text-muted">
            Questions flagged by the anomaly sweep — a very low correct rate across enough attempts
            usually means a mis-keyed answer or an ambiguous stem, not a hard question.
          </p>
        </div>
        <button type="button" className="btn-secondary w-full sm:w-auto" onClick={() => void refill()}>
          Queue a pool refill
        </button>
      </header>

      {error ? <ErrorNotice message={error} /> : null}

      <section className="card space-y-3 p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Email</h2>
            <p className="mt-1 text-sm text-muted">
              Sends a real message through Resend to your own account email, to confirm the sending
              domain and API key without registering a throwaway account.
            </p>
          </div>
          <button type="button" className="btn-secondary w-full shrink-0 sm:w-auto" onClick={() => void sendTestEmail()} disabled={emailBusy}>
            {emailBusy ? 'Sending…' : 'Send test email'}
          </button>
        </div>

        {emailResult ? (
          <Callout>
            Sent to <strong>{emailResult.to}</strong> from <strong>{emailResult.from}</strong>
            {emailResult.replyTo ? (
              <>
                , with replies going to <strong>{emailResult.replyTo}</strong>
              </>
            ) : null}
            . Check that inbox — including spam, the first time.
          </Callout>
        ) : null}

        {emailNotice ? <Callout tone="warn">{emailNotice}</Callout> : null}
      </section>

      {stats ? (
        <section className="card p-4 sm:p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">Stats</h2>
          <pre className="mt-2.5 overflow-x-auto rounded-lg bg-raised p-3 text-xs text-muted">
            {JSON.stringify(stats, null, 2)}
          </pre>
        </section>
      ) : null}

      {!data?.questions.length ? (
        <EmptyState title="Nothing flagged" body="No questions currently need review. The sweep runs every six hours." />
      ) : (
        <ol className="space-y-4">
          {data.questions.map((q) => (
            <li key={q.id} className="card p-4 sm:p-6">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="badge bg-raised text-muted">{q.examCode}</span>
                <span className="badge bg-raised text-muted">{q.domain.code} · {q.domain.name}</span>
                <span className="badge bg-raised text-muted capitalize">{q.difficulty}</span>
                <span className="badge bg-raised text-muted">{q.source}</span>
                {q.correctRatePct !== null ? (
                  <span className="badge bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-200">
                    {q.correctRatePct}% correct over {q.timesServed} attempts
                  </span>
                ) : null}
              </div>

              {q.flagReason ? (
                <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-500/10 p-3 text-sm text-amber-900 dark:text-amber-200">{q.flagReason}</p>
              ) : null}

              <p className="mt-3 text-sm leading-relaxed text-ink">{q.stem}</p>

              {q.options ? (
                <ul className="mt-3 space-y-1.5">
                  {q.options.map((o) => (
                    <li
                      key={o}
                      className={`rounded-lg border p-2.5 text-sm ${
                        o === q.correctAnswer ? 'border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10' : 'border-line'
                      }`}
                    >
                      {o}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm">
                  <span className="text-muted">Answer: </span>
                  <span className="font-mono font-semibold">{q.correctAnswer}</span>
                </p>
              )}

              <p className="mt-3 rounded-lg bg-raised p-3 text-sm text-muted">{q.explanation}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className="btn-secondary" onClick={() => void act(q.id, 'approve')}>
                  Keep (reset counters)
                </button>
                <button
                  type="button"
                  className="btn border border-red-300 dark:border-red-500/40 bg-surface text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/15"
                  onClick={() => void act(q.id, 'retire')}
                >
                  Retire
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
